import React, {memo, useCallback, useEffect, useRef} from "react";

import {AugmentedEvent, SimulationEventType} from "@repo/shared";
import {AdditiveBlending, Color} from "three";

import {useGame} from "~/contexts/game-manager/GameManagerProvider";
import {ParticleConfig} from "~/three/particle-effect/types";

import {ParticleEffect, ParticleEffectRef} from "../particle-effect";

// --- Helper functions and constants (no changes needed) ---
const randomColor = (colors: Color[]): Color => {
  return colors[Math.floor(Math.random() * colors.length)]!;
};

const fireworkColors = [
  new Color("#FF6060"),
  new Color("#60FF60"),
  new Color("#6060FF"),
  new Color("#FFFF60"),
  new Color("#FF60FF"),
  new Color("#60FFFF"),
];

const primaryTrailOptions = (color: Color): ParticleConfig => ({
  particleLifetime: 0.7,
  lifetimeRandomness: 0.2,
  velocityMultiplier: 18,
  upwardBias: 0,
  gravity: 6,
  size: [
    {stop: 0, size: 0.2},
    {stop: 0.5, size: 0.6},
    {stop: 1, size: 0.1},
  ],
  color: [
    {stop: 0, color: new Color("white")},
    {stop: 0.3, color: color},
  ],
});

/**
 * Listens for the 'round-finished' event and triggers a firework explosion
 * at the position of the player who completed the level.
 */
const GoalExplosion: React.FC = memo(() => {
  const {gameManager} = useGame();
  const particleEffectRef = useRef<ParticleEffectRef>(null);

  // A ref to store the position of the last shard hit, which is where the firework should launch from.
  const lastShardHitPosition = useRef<{x: number; y: number} | null>(null);

  const emitLauncher = useCallback((x: number, y: number) => {
    const color = randomColor(fireworkColors);
    particleEffectRef.current?.emit({
      x,
      y,
      count: 16,
      spawnRate: 128,
      particleLifetime: 0.8,
      lifetimeRandomness: 0.25,
      velocityMultiplier: 20,
      upwardBias: 5,
      gravity: 5,
      size: [
        {stop: 0, size: 0.4},
        {stop: 1, size: 0.1},
      ],
      color: new Color("#CCCCCC"),
      spawnEvents: [
        {time: 0.99, count: 16, options: primaryTrailOptions(color)},
      ],
    });
  }, []);

  // This effect sets up all the event listeners.
  useEffect(() => {
    if (!gameManager) return;

    // Listener 1: Track the position of the last primary shard hit.
    const handleSimulationEvent = (event: AugmentedEvent) => {
      if (
        event.isPrimary &&
        event.type === SimulationEventType.PlayerHitShard
      ) {
        lastShardHitPosition.current = {
          x: event.player.currentPosition.x,
          y: event.player.currentPosition.y,
        };
      }
    };

    // Listener 2: React to the round finishing.
    const handleRoundFinished = (
      result: {completed: boolean},
      isPrimary: boolean,
    ) => {
      // Only trigger for a successful completion of the primary round.
      if (isPrimary && result.completed) {
        // Use the stored position of the final shard hit.
        if (lastShardHitPosition.current) {
          emitLauncher(
            lastShardHitPosition.current.x,
            lastShardHitPosition.current.y,
          );
        }
      }
    };

    // Listener 3: Reset state when a new round starts.
    const handleRoundStarted = (_server: any, isPrimary: any) => {
      if (isPrimary) {
        lastShardHitPosition.current = null;
      }
    };

    // Subscribe to all necessary events.
    gameManager.on("simulation-event", handleSimulationEvent);
    gameManager.on("round-finished", handleRoundFinished);
    gameManager.on("round-started", handleRoundStarted);

    // Return the cleanup function to unsubscribe from all events.
    return () => {
      gameManager.off("simulation-event", handleSimulationEvent);
      gameManager.off("round-finished", handleRoundFinished);
      gameManager.off("round-started", handleRoundStarted);
    };
  }, [gameManager, emitLauncher]);

  return (
    <ParticleEffect ref={particleEffectRef} maxParticles={512} zIndex={5}>
      <circleGeometry args={[0.5, 16]} />
      <meshBasicMaterial
        blending={AdditiveBlending}
        depthWrite={false}
        transparent={true}
      />
    </ParticleEffect>
  );
});

export default GoalExplosion;
